// ==================== ADMIN ROAD FATALITIES ====================
// Admin interface for managing road accident submissions

import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, Check, X, Eye, Edit2, Trash2, MapPin, Calendar,
  ExternalLink, User, Filter, ChevronDown, ChevronUp, Search,
  Loader2, RefreshCw, FileText, Shield, Clock, ArrowLeft, Skull,
  Car, Bike, PersonStanding, Bus, Truck, CheckCircle, XCircle,
  AlertCircle, Plus, Save, Globe, EyeOff, ToggleLeft, ToggleRight,
  Upload, Download, UserPlus, FileJson, Table
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { roadFatalities, appSettings } from '../lib/supabase';
import { PARISHES } from '../lib/constants';

// Vehicle type configs
const VEHICLE_TYPES = {
  car: { icon: Car, label: 'Car' },
  motorcycle: { icon: Bike, label: 'Motorcycle' },
  bicycle: { icon: Bike, label: 'Bicycle' },
  pedestrian: { icon: PersonStanding, label: 'Pedestrian' },
  bus: { icon: Bus, label: 'Bus' },
  truck: { icon: Truck, label: 'Truck' },
  minibus: { icon: Bus, label: 'Minibus' }
};

const AGE_GROUPS = {
  child: 'Child (0-12)',
  teen: 'Teen (13-19)',
  young_adult: 'Young Adult (20-39)',
  adult: 'Adult (40-59)',
  senior: 'Senior (60+)',
  unknown: 'Unknown'
};

const VICTIM_ROLES = {
  driver: 'Driver',
  passenger: 'Passenger',
  pedestrian: 'Pedestrian',
  cyclist: 'Cyclist',
  pillion: 'Pillion Rider',
  unknown: 'Unknown'
};

// Parish coordinates for location
const PARISH_COORDS = {
  'Kingston': { lat: 17.9714, lng: -76.7920 },
  'St. Andrew': { lat: 18.0179, lng: -76.7674 },
  'St. Thomas': { lat: 17.9500, lng: -76.3500 },
  'Portland': { lat: 18.1667, lng: -76.4500 },
  'St. Mary': { lat: 18.2667, lng: -76.9000 },
  'St. Ann': { lat: 18.4074, lng: -77.1050 },
  'Trelawny': { lat: 18.3500, lng: -77.6000 },
  'St. James': { lat: 18.4667, lng: -77.9167 },
  'Hanover': { lat: 18.4000, lng: -78.1333 },
  'Westmoreland': { lat: 18.2833, lng: -78.1500 },
  'St. Elizabeth': { lat: 18.0500, lng: -77.8500 },
  'Manchester': { lat: 18.0667, lng: -77.5000 },
  'Clarendon': { lat: 17.9500, lng: -77.2333 },
  'St. Catherine': { lat: 17.9917, lng: -77.0000 }
};

const ACCIDENT_TYPES = [
  { value: 'collision', label: 'Collision' },
  { value: 'hit_and_run', label: 'Hit and Run' },
  { value: 'overturned', label: 'Overturned' },
  { value: 'pedestrian_struck', label: 'Pedestrian Struck' },
  { value: 'motorcycle_crash', label: 'Motorcycle Crash' },
  { value: 'other', label: 'Other' }
];

const AdminRoadFatalities = ({ onBack, showToast, isPublic, setIsPublic }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [accidents, setAccidents] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [selectedAccident, setSelectedAccident] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [togglingVisibility, setTogglingVisibility] = useState(false);

  // New states for Quick Add and Import
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importData, setImportData] = useState('');
  const [importFormat, setImportFormat] = useState('json');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  // Quick Add form state
  const emptyForm = {
    accident_date: new Date().toISOString().split('T')[0],
    accident_time: '',
    parish: '',
    city: '',
    location_description: '',
    road_name: '',
    road_type: 'main_road',
    accident_type: 'collision',
    vehicles_involved: [],
    fatality_count: 1,
    injury_count: 0,
    source_url: '',
    source_name: '',
    source_date: '',
    summary: '',
    victims: [{ age: '', gender: '', role: 'unknown', parish_of_origin: '' }]
  };
  const [addForm, setAddForm] = useState(emptyForm);

  // Toggle public visibility
  const togglePublicVisibility = async () => {
    setTogglingVisibility(true);
    try {
      const newValue = !isPublic;
      await appSettings.set('fatality_tracker_public', newValue.toString());
      setIsPublic(newValue);
      showToast?.(`Road Fatality Map is now ${newValue ? 'visible to the public' : 'hidden from public (admin only)'}`, 'success');
    } catch (error) {
      console.error('Error toggling visibility:', error);
      showToast?.('Failed to update visibility', 'error');
    } finally {
      setTogglingVisibility(false);
    }
  };

  // Filters
  const [filters, setFilters] = useState({
    status: 'pending',
    year: '',
    parish: '',
    search: ''
  });

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [accidentsRes, countRes] = await Promise.all([
        roadFatalities.getAllAdmin(filters),
        roadFatalities.getPendingCount()
      ]);

      if (accidentsRes.data) {
        // Apply search filter client-side
        let filtered = accidentsRes.data;
        if (filters.search) {
          const search = filters.search.toLowerCase();
          filtered = filtered.filter(a =>
            a.location_description?.toLowerCase().includes(search) ||
            a.city?.toLowerCase().includes(search) ||
            a.parish?.toLowerCase().includes(search) ||
            a.road_name?.toLowerCase().includes(search) ||
            a.source_url?.toLowerCase().includes(search)
          );
        }
        setAccidents(filtered);
      }
      if (countRes) setPendingCount(countRes.count);
    } catch (error) {
      console.error('Error loading data:', error);
      showToast?.('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, showToast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Verify accident
  const handleVerify = async (id) => {
    setProcessing(true);
    try {
      const { error } = await roadFatalities.verify(id, user?.id);
      if (error) throw error;
      showToast?.('Accident verified successfully', 'success');
      setSelectedAccident(null);
      loadData();
    } catch (error) {
      console.error('Verify error:', error);
      showToast?.('Failed to verify accident', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Reject accident
  const handleReject = async (id, reason = '') => {
    setProcessing(true);
    try {
      const { error } = await roadFatalities.reject(id, user?.id, reason);
      if (error) throw error;
      showToast?.('Accident rejected', 'success');
      setSelectedAccident(null);
      loadData();
    } catch (error) {
      console.error('Reject error:', error);
      showToast?.('Failed to reject accident', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Delete accident
  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this accident record? This cannot be undone.')) {
      return;
    }
    setProcessing(true);
    try {
      const { error } = await roadFatalities.delete(id);
      if (error) throw error;
      showToast?.('Accident deleted', 'success');
      setSelectedAccident(null);
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      showToast?.('Failed to delete accident', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Update accident
  const handleUpdate = async () => {
    if (!editForm) return;
    setProcessing(true);
    try {
      const { error } = await roadFatalities.update(editForm.id, editForm);
      if (error) throw error;
      showToast?.('Accident updated successfully', 'success');
      setEditMode(false);
      setEditForm(null);
      loadData();
    } catch (error) {
      console.error('Update error:', error);
      showToast?.('Failed to update accident', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Start editing
  const startEdit = (accident) => {
    setEditForm({
      ...accident,
      victims: accident.victims || []
    });
    setEditMode(true);
    setSelectedAccident(null);
  };

  // Quick Add - Submit new accident
  const handleQuickAdd = async () => {
    if (!addForm.accident_date || !addForm.parish) {
      showToast?.('Date and Parish are required', 'error');
      return;
    }
    if (!addForm.source_url && !addForm.source_name) {
      showToast?.('Please provide a source URL or name', 'error');
      return;
    }

    setProcessing(true);
    try {
      // Add parish coordinates if not provided
      const coords = PARISH_COORDS[addForm.parish] || {};
      const dataToSubmit = {
        ...addForm,
        latitude: addForm.latitude || coords.lat,
        longitude: addForm.longitude || coords.lng,
        fatality_count: parseInt(addForm.fatality_count) || 1,
        injury_count: parseInt(addForm.injury_count) || 0,
        victims: addForm.victims.filter(v => v.age || v.gender || v.role !== 'unknown')
      };

      const { error } = await roadFatalities.createAdmin(dataToSubmit, user?.id);
      if (error) throw error;

      showToast?.('Accident added and verified successfully', 'success');
      setShowAddForm(false);
      setAddForm(emptyForm);
      loadData();
    } catch (error) {
      console.error('Quick add error:', error);
      showToast?.('Failed to add accident', 'error');
    } finally {
      setProcessing(false);
    }
  };

  // Add victim to form
  const addVictimToForm = (formSetter) => {
    formSetter(f => ({
      ...f,
      victims: [...(f.victims || []), { age: '', gender: '', role: 'unknown', parish_of_origin: '' }]
    }));
  };

  // Remove victim from form
  const removeVictimFromForm = (formSetter, index) => {
    formSetter(f => ({
      ...f,
      victims: f.victims.filter((_, i) => i !== index)
    }));
  };

  // Update victim in form
  const updateVictimInForm = (formSetter, index, field, value) => {
    formSetter(f => ({
      ...f,
      victims: f.victims.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  // Toggle vehicle type
  const toggleVehicle = (formSetter, vehicle) => {
    formSetter(f => ({
      ...f,
      vehicles_involved: f.vehicles_involved?.includes(vehicle)
        ? f.vehicles_involved.filter(v => v !== vehicle)
        : [...(f.vehicles_involved || []), vehicle]
    }));
  };

  // Parse CSV data
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const records = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const record = {};
      headers.forEach((h, idx) => {
        let val = values[idx] || '';
        // Handle special fields
        if (h === 'vehicles_involved') {
          record[h] = val ? val.split(';').map(v => v.trim()) : [];
        } else if (h === 'fatality_count' || h === 'injury_count' || h === 'age') {
          record[h] = val ? parseInt(val) : null;
        } else if (h === 'latitude' || h === 'longitude') {
          record[h] = val ? parseFloat(val) : null;
        } else {
          record[h] = val || null;
        }
      });
      records.push(record);
    }
    return records;
  };

  // Handle bulk import
  const handleImport = async () => {
    if (!importData.trim()) {
      showToast?.('Please paste data to import', 'error');
      return;
    }

    setImporting(true);
    setImportResults(null);

    try {
      let records = [];

      if (importFormat === 'json') {
        try {
          const parsed = JSON.parse(importData);
          records = Array.isArray(parsed) ? parsed : [parsed];
        } catch {
          showToast?.('Invalid JSON format', 'error');
          setImporting(false);
          return;
        }
      } else {
        records = parseCSV(importData);
      }

      if (records.length === 0) {
        showToast?.('No valid records found', 'error');
        setImporting(false);
        return;
      }

      // Process each record - add victims array if not present
      records = records.map(r => ({
        ...r,
        victims: r.victims || []
      }));

      const result = await roadFatalities.bulkImport(records, user?.id);
      setImportResults(result);

      if (result.imported > 0) {
        showToast?.(`Imported ${result.imported} accidents successfully`, 'success');
        loadData();
      }
      if (result.failed > 0) {
        showToast?.(`${result.failed} records failed to import`, 'error');
      }
    } catch (error) {
      console.error('Import error:', error);
      showToast?.('Import failed: ' + error.message, 'error');
    } finally {
      setImporting(false);
    }
  };

  // Download CSV template
  const downloadTemplate = () => {
    const headers = 'accident_date,accident_time,parish,city,location_description,road_name,road_type,accident_type,vehicles_involved,fatality_count,injury_count,source_url,source_name,summary';
    const example = '2026-01-15,14:30:00,St. Andrew,Kingston,Half Way Tree intersection,Half Way Tree Road,main_road,collision,car;motorcycle,2,1,https://example.com/article,Jamaica Gleaner,Two people killed in collision';
    const csv = `${headers}\n${example}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'accident_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Get status badge
  const getStatusBadge = (accident) => {
    if (accident.is_verified) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Verified
        </span>
      );
    }
    if (accident.reviewed_at) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Rejected
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
        <Clock className="w-3 h-3" />
        Pending
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 text-white">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                title="Bulk Import"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Import</span>
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 px-3 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors"
                title="Quick Add"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline text-sm">Add</span>
              </button>
              <button
                onClick={loadData}
                disabled={loading}
                className="p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/20 rounded-xl">
                <Shield className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Road Fatality Map</h1>
                <p className="text-red-100">Manage and verify accident submissions</p>
              </div>
            </div>

            {/* Visibility Toggle */}
            <button
              onClick={togglePublicVisibility}
              disabled={togglingVisibility}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                isPublic
                  ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              title={isPublic ? 'Click to hide from public' : 'Click to make visible to public'}
            >
              {togglingVisibility ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : isPublic ? (
                <Globe className="w-5 h-5" />
              ) : (
                <EyeOff className="w-5 h-5" />
              )}
              <span className="hidden sm:inline">{isPublic ? 'Public' : 'Admin Only'}</span>
            </button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-2xl font-bold">{pendingCount}</div>
              <div className="text-xs text-red-100">Pending Review</div>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-2xl font-bold">{accidents.filter(a => a.is_verified).length}</div>
              <div className="text-xs text-red-100">Verified</div>
            </div>
            <div className="bg-white/10 rounded-xl p-3">
              <div className="text-2xl font-bold">{accidents.length}</div>
              <div className="text-xs text-red-100">Total Records</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            {/* Status Tabs */}
            <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
              {[
                { id: 'pending', label: 'Pending', count: pendingCount },
                { id: 'verified', label: 'Verified' },
                { id: '', label: 'All' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setFilters(f => ({ ...f, status: tab.id }))}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    filters.status === tab.id
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab.label}
                  {tab.count > 0 && (
                    <span className="px-1.5 py-0.5 bg-red-100 text-red-600 rounded-full text-xs">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                  className="pl-9 pr-4 py-2 border rounded-lg text-sm w-48 focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Filter Toggle */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                  showFilters ? 'bg-red-50 border-red-200 text-red-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Filter className="w-4 h-4" />
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="pt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters(f => ({ ...f, year: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Years</option>
                  {Array.from({ length: Math.max(2026, new Date().getFullYear()) - 2023 }, (_, i) => Math.max(2026, new Date().getFullYear()) - i).map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Parish</label>
                <select
                  value={filters.parish}
                  onChange={(e) => setFilters(f => ({ ...f, parish: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                >
                  <option value="">All Parishes</option>
                  {PARISHES.map(p => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-red-600" />
          </div>
        ) : accidents.length === 0 ? (
          <div className="text-center py-20">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">No accident records found</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Fatalities</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Source</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {accidents.map(accident => (
                    <tr key={accident.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium">
                          {new Date(accident.accident_date).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-500">
                          {accident.accident_time || 'Time unknown'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                          {accident.location_description || accident.city || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">{accident.parish}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-red-600">
                          <Skull className="w-4 h-4" />
                          <span className="font-bold">{accident.fatality_count}</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {accident.victims?.length || 0} victim(s)
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">{accident.source_name || 'Unknown'}</div>
                        {accident.source_url && (
                          <a
                            href={accident.source_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(accident)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => setSelectedAccident(accident)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4 text-gray-600" />
                          </button>
                          <button
                            onClick={() => startEdit(accident)}
                            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4 text-gray-600" />
                          </button>
                          {!accident.is_verified && !accident.reviewed_at && (
                            <>
                              <button
                                onClick={() => handleVerify(accident.id)}
                                disabled={processing}
                                className="p-1.5 hover:bg-emerald-100 rounded-lg transition-colors"
                                title="Verify"
                              >
                                <Check className="w-4 h-4 text-emerald-600" />
                              </button>
                              <button
                                onClick={() => handleReject(accident.id)}
                                disabled={processing}
                                className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                                title="Reject"
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => handleDelete(accident.id)}
                            disabled={processing}
                            className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedAccident && !editMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setSelectedAccident(null)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold">Accident Details</h2>
                {getStatusBadge(selectedAccident)}
              </div>
              <button onClick={() => setSelectedAccident(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Fatality Count */}
              <div className="flex items-center justify-center p-4 bg-red-50 rounded-xl">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-red-600 mb-1">
                    <Skull className="w-8 h-8" />
                    <span className="text-4xl font-bold">{selectedAccident.fatality_count}</span>
                  </div>
                  <div className="text-sm text-red-700">Fatalities</div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Date</div>
                  <div className="font-medium">{new Date(selectedAccident.accident_date).toLocaleDateString()}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Time</div>
                  <div className="font-medium">{selectedAccident.accident_time || 'Unknown'}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg col-span-2">
                  <div className="text-xs text-gray-500 mb-1">Location</div>
                  <div className="font-medium">{selectedAccident.location_description || 'Unknown'}</div>
                  <div className="text-sm text-gray-600">
                    {selectedAccident.road_name && `${selectedAccident.road_name}, `}
                    {selectedAccident.city && `${selectedAccident.city}, `}
                    {selectedAccident.parish}
                  </div>
                </div>
              </div>

              {/* Vehicles */}
              {selectedAccident.vehicles_involved?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Vehicles Involved</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedAccident.vehicles_involved.map(type => {
                      const config = VEHICLE_TYPES[type] || { icon: Car, label: type };
                      const Icon = config.icon;
                      return (
                        <span key={type} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 rounded-full text-sm">
                          <Icon className="w-4 h-4" />
                          {config.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Victims */}
              {selectedAccident.victims?.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-700 mb-2">Victims ({selectedAccident.victims.length})</div>
                  <div className="space-y-2">
                    {selectedAccident.victims.map((victim, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-gray-500" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">
                            {victim.age ? `${victim.age} years old` : 'Age unknown'}
                            {victim.gender && ` • ${victim.gender}`}
                          </div>
                          <div className="text-sm text-gray-500">
                            {VICTIM_ROLES[victim.role] || victim.role}
                            {victim.parish_of_origin && ` • From ${victim.parish_of_origin}`}
                          </div>
                          <div className="text-xs text-gray-400">
                            Age Group: {AGE_GROUPS[victim.age_group] || 'Unknown'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              {selectedAccident.summary && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="text-xs text-gray-500 mb-1">Summary</div>
                  <div className="text-sm">{selectedAccident.summary}</div>
                </div>
              )}

              {/* Source */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="text-xs text-blue-600 mb-1">Source</div>
                <div className="font-medium text-blue-800">{selectedAccident.source_name || 'Unknown'}</div>
                {selectedAccident.source_url && (
                  <a
                    href={selectedAccident.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <ExternalLink className="w-4 h-4" />
                    {selectedAccident.source_url}
                  </a>
                )}
              </div>

              {/* Submitter Info */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="text-xs text-gray-500 mb-1">Submitted By</div>
                <div className="font-medium">{selectedAccident.submitter?.full_name || 'Anonymous'}</div>
                <div className="text-xs text-gray-400">
                  {new Date(selectedAccident.created_at).toLocaleString()}
                </div>
              </div>

              {/* Action Buttons */}
              {!selectedAccident.is_verified && !selectedAccident.reviewed_at && (
                <div className="flex gap-3">
                  <button
                    onClick={() => handleVerify(selectedAccident.id)}
                    disabled={processing}
                    className="flex-1 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Check className="w-5 h-5" />
                    Verify
                  </button>
                  <button
                    onClick={() => handleReject(selectedAccident.id)}
                    disabled={processing}
                    className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <X className="w-5 h-5" />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editMode && editForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setEditMode(false); setEditForm(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold">Edit Accident Record</h2>
              <button onClick={() => { setEditMode(false); setEditForm(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                  <input
                    type="date"
                    value={editForm.accident_date?.split('T')[0] || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, accident_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                  <input
                    type="time"
                    value={editForm.accident_time || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, accident_time: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parish</label>
                  <select
                    value={editForm.parish || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, parish: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    <option value="">Select parish...</option>
                    {PARISHES.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City/Town</label>
                  <input
                    type="text"
                    value={editForm.city || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Location Description</label>
                  <input
                    type="text"
                    value={editForm.location_description || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, location_description: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Road Name</label>
                  <input
                    type="text"
                    value={editForm.road_name || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, road_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fatality Count</label>
                  <input
                    type="number"
                    min="1"
                    value={editForm.fatality_count || 1}
                    onChange={(e) => setEditForm(f => ({ ...f, fatality_count: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Injury Count</label>
                  <input
                    type="number"
                    min="0"
                    value={editForm.injury_count || 0}
                    onChange={(e) => setEditForm(f => ({ ...f, injury_count: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Latitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editForm.latitude || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, latitude: parseFloat(e.target.value) || null }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g., 18.0179"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Longitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={editForm.longitude || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, longitude: parseFloat(e.target.value) || null }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g., -76.7897"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source URL</label>
                  <input
                    type="url"
                    value={editForm.source_url || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, source_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
                  <textarea
                    rows={3}
                    value={editForm.summary || ''}
                    onChange={(e) => setEditForm(f => ({ ...f, summary: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Victims Section in Edit */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Victims ({editForm.victims?.length || 0})</label>
                  <button
                    type="button"
                    onClick={() => addVictimToForm(setEditForm)}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    <UserPlus className="w-3 h-3" />
                    Add Victim
                  </button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {editForm.victims?.map((victim, idx) => (
                    <div key={idx} className="flex gap-2 items-center p-2 bg-gray-50 rounded-lg">
                      <input
                        type="number"
                        placeholder="Age"
                        value={victim.age || ''}
                        onChange={(e) => updateVictimInForm(setEditForm, idx, 'age', e.target.value ? parseInt(e.target.value) : '')}
                        className="w-16 px-2 py-1 border rounded text-sm"
                      />
                      <select
                        value={victim.gender || ''}
                        onChange={(e) => updateVictimInForm(setEditForm, idx, 'gender', e.target.value)}
                        className="px-2 py-1 border rounded text-sm"
                      >
                        <option value="">Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <select
                        value={victim.role || 'unknown'}
                        onChange={(e) => updateVictimInForm(setEditForm, idx, 'role', e.target.value)}
                        className="px-2 py-1 border rounded text-sm"
                      >
                        {Object.entries(VICTIM_ROLES).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeVictimFromForm(setEditForm, idx)}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => { setEditMode(false); setEditForm(null); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdate}
                  disabled={processing}
                  className="flex-1 py-3 bg-red-600 text-white font-medium rounded-xl hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5" />
                  )}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Add Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowAddForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Plus className="w-6 h-6" />
                <h2 className="text-lg font-bold">Quick Add Accident</h2>
              </div>
              <button onClick={() => setShowAddForm(false)} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                  <input
                    type="date"
                    value={addForm.accident_date}
                    onChange={(e) => setAddForm(f => ({ ...f, accident_date: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Time</label>
                  <input
                    type="time"
                    value={addForm.accident_time}
                    onChange={(e) => setAddForm(f => ({ ...f, accident_time: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parish *</label>
                  <select
                    value={addForm.parish}
                    onChange={(e) => setAddForm(f => ({ ...f, parish: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select parish...</option>
                    {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">City/Town</label>
                  <input
                    type="text"
                    value={addForm.city}
                    onChange={(e) => setAddForm(f => ({ ...f, city: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g., Portmore"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Road Name</label>
                  <input
                    type="text"
                    value={addForm.road_name}
                    onChange={(e) => setAddForm(f => ({ ...f, road_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="e.g., North Coast Highway"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Accident Type</label>
                  <select
                    value={addForm.accident_type}
                    onChange={(e) => setAddForm(f => ({ ...f, accident_type: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    {ACCIDENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
              </div>

              {/* Location Description */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Location Description</label>
                <input
                  type="text"
                  value={addForm.location_description}
                  onChange={(e) => setAddForm(f => ({ ...f, location_description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="e.g., Near Royalton Resort, intersection of Main St and Cross Rd"
                />
              </div>

              {/* Vehicles */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Vehicles Involved</label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(VEHICLE_TYPES).map(([key, config]) => {
                    const Icon = config.icon;
                    const selected = addForm.vehicles_involved?.includes(key);
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleVehicle(setAddForm, key)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          selected ? 'bg-emerald-100 text-emerald-700 border-emerald-300 border' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {config.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Fatalities & Injuries */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fatalities *</label>
                  <input
                    type="number"
                    min="1"
                    value={addForm.fatality_count}
                    onChange={(e) => setAddForm(f => ({ ...f, fatality_count: parseInt(e.target.value) || 1 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Injuries</label>
                  <input
                    type="number"
                    min="0"
                    value={addForm.injury_count}
                    onChange={(e) => setAddForm(f => ({ ...f, injury_count: parseInt(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Victims */}
              <div className="border rounded-xl p-4 bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-700">Victim Details ({addForm.victims?.length || 0})</label>
                  <button
                    type="button"
                    onClick={() => addVictimToForm(setAddForm)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                  >
                    <UserPlus className="w-4 h-4" />
                    Add Victim
                  </button>
                </div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {addForm.victims?.map((victim, idx) => (
                    <div key={idx} className="flex flex-wrap gap-2 items-center p-3 bg-white rounded-lg border">
                      <input
                        type="number"
                        placeholder="Age"
                        value={victim.age || ''}
                        onChange={(e) => updateVictimInForm(setAddForm, idx, 'age', e.target.value ? parseInt(e.target.value) : '')}
                        className="w-20 px-2 py-1.5 border rounded text-sm"
                      />
                      <select
                        value={victim.gender || ''}
                        onChange={(e) => updateVictimInForm(setAddForm, idx, 'gender', e.target.value)}
                        className="px-2 py-1.5 border rounded text-sm"
                      >
                        <option value="">Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <select
                        value={victim.role || 'unknown'}
                        onChange={(e) => updateVictimInForm(setAddForm, idx, 'role', e.target.value)}
                        className="px-2 py-1.5 border rounded text-sm"
                      >
                        {Object.entries(VICTIM_ROLES).map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                      </select>
                      <select
                        value={victim.parish_of_origin || ''}
                        onChange={(e) => updateVictimInForm(setAddForm, idx, 'parish_of_origin', e.target.value)}
                        className="px-2 py-1.5 border rounded text-sm"
                      >
                        <option value="">From Parish</option>
                        {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <button
                        type="button"
                        onClick={() => removeVictimFromForm(setAddForm, idx)}
                        className="p-1.5 hover:bg-red-100 rounded text-red-600 ml-auto"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Source */}
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source URL *</label>
                  <input
                    type="url"
                    value={addForm.source_url}
                    onChange={(e) => setAddForm(f => ({ ...f, source_url: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="https://jamaica-gleaner.com/..."
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Source Name</label>
                  <input
                    type="text"
                    value={addForm.source_name}
                    onChange={(e) => setAddForm(f => ({ ...f, source_name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Jamaica Gleaner"
                  />
                </div>
              </div>

              {/* Summary */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Summary</label>
                <textarea
                  rows={3}
                  value={addForm.summary}
                  onChange={(e) => setAddForm(f => ({ ...f, summary: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm"
                  placeholder="Brief description of what happened..."
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => { setShowAddForm(false); setAddForm(emptyForm); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleQuickAdd}
                  disabled={processing}
                  className="flex-1 py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5" />
                  )}
                  Add & Verify
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => { setShowImportModal(false); setImportData(''); setImportResults(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-3">
                <Upload className="w-6 h-6" />
                <h2 className="text-lg font-bold">Bulk Import</h2>
              </div>
              <button onClick={() => { setShowImportModal(false); setImportData(''); setImportResults(null); }} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Format Selection */}
              <div className="flex gap-2">
                <button
                  onClick={() => setImportFormat('json')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    importFormat === 'json' ? 'bg-blue-100 text-blue-700 border-blue-300 border' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <FileJson className="w-4 h-4" />
                  JSON
                </button>
                <button
                  onClick={() => setImportFormat('csv')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                    importFormat === 'csv' ? 'bg-blue-100 text-blue-700 border-blue-300 border' : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  <Table className="w-4 h-4" />
                  CSV
                </button>
                <button
                  onClick={downloadTemplate}
                  className="flex items-center gap-2 px-4 py-2 ml-auto bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Template
                </button>
              </div>

              {/* Data Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste {importFormat.toUpperCase()} Data
                </label>
                <textarea
                  rows={12}
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500"
                  placeholder={importFormat === 'json'
                    ? `[\n  {\n    "accident_date": "2026-01-15",\n    "parish": "St. Andrew",\n    "fatality_count": 2,\n    "source_url": "https://..."\n  }\n]`
                    : 'accident_date,parish,fatality_count,source_url\n2026-01-15,St. Andrew,2,https://...'
                  }
                />
              </div>

              {/* Import Results */}
              {importResults && (
                <div className={`p-4 rounded-lg ${importResults.failed > 0 ? 'bg-yellow-50 border border-yellow-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1 text-emerald-700">
                      <CheckCircle className="w-4 h-4" />
                      {importResults.imported} imported
                    </div>
                    {importResults.failed > 0 && (
                      <div className="flex items-center gap-1 text-red-700">
                        <XCircle className="w-4 h-4" />
                        {importResults.failed} failed
                      </div>
                    )}
                  </div>
                  {importResults.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-600 max-h-20 overflow-y-auto">
                      {importResults.errors.map((err, i) => (
                        <div key={i}>Row {i + 1}: {err.error}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => { setShowImportModal(false); setImportData(''); setImportResults(null); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing || !importData.trim()}
                  className="flex-1 py-3 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {importing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5" />
                  )}
                  Import Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRoadFatalities;
